import React, { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate, formatDate } from '@/lib/utils'
import {
  Plus, X, Send, Mail, Smartphone, Users, FileText,
  Zap, Play, Pause, Trash2, Copy, Clock, CheckCircle2,
  BarChart2, ChevronRight, Edit3, Filter, Tag,
  ArrowRight, AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'

// ─── Types ────────────────────────────────────────────────────────────────────
type Tab = 'campaigns' | 'sequences' | 'templates' | 'segments'

const CHANNEL_CONFIG: Record<string, { color: string; bg: string; icon: any; label: string }> = {
  email: { color: 'var(--info)',     bg: 'color-mix(in srgb, var(--info) var(--status-tint-fill), transparent)',     icon: Mail,       label: 'Email' },
  sms:   { color: 'var(--positive)', bg: 'color-mix(in srgb, var(--positive) var(--status-tint-fill), transparent)', icon: Smartphone, label: 'SMS' },
}

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
  draft:     { color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)',                                                          label: 'Draft' },
  scheduled: { color: 'var(--warning)',      bg: 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)',      label: 'Scheduled' },
  sending:   { color: 'var(--info)',         bg: 'color-mix(in srgb, var(--info) var(--status-tint-fill), transparent)',         label: 'Sending' },
  sent:      { color: 'var(--positive)',     bg: 'color-mix(in srgb, var(--positive) var(--status-tint-fill), transparent)',     label: 'Sent' },
  active:    { color: 'var(--positive)',     bg: 'color-mix(in srgb, var(--positive) var(--status-tint-fill), transparent)',     label: 'Active' },
  paused:    { color: 'var(--warning)',      bg: 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)',      label: 'Paused' },
  archived:  { color: 'var(--border-default)', bg: 'var(--hover-bg)',                                                            label: 'Archived' },
}

const TRIGGER_TYPES = [
  { value: 'manual', label: 'Manual enrollment' },
  { value: 'contact_created', label: 'Contact created' },
  { value: 'deal_won', label: 'Deal won' },
  { value: 'invoice_paid', label: 'Invoice paid' },
  { value: 'job_complete', label: 'Job completed' },
]

const TEMPLATE_CATEGORIES = ['Welcome', 'Follow-up', 'Promotion', 'Re-engagement', 'Transactional', 'Other']

const FILTER_FIELDS = [
  { value: 'status', label: 'Contact status' },
  { value: 'company_name', label: 'Company' },
  { value: 'tags', label: 'Tags' },
  { value: 'created_at', label: 'Created date' },
]

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  background: 'var(--hover-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
  outline: 'none', boxSizing: 'border-box',
}

// ─── Campaign Builder Modal ───────────────────────────────────────────────────
function CampaignModal({ onClose, onSaved, campaign, segments }: any) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isEdit = !!campaign
  const [form, setForm] = useState({
    name: campaign?.name || '',
    channel: campaign?.channel || 'email',
    subject: campaign?.subject || '',
    body: campaign?.body || '',
    segment_id: campaign?.segment_id || '',
    segment_name: campaign?.segment_name || '',
    scheduled_at: campaign?.scheduled_at ? campaign.scheduled_at.split('.')[0] : '',
    status: campaign?.status || 'draft',
  })
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'content' | 'audience' | 'schedule'>('content')
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async (sendNow = false) => {
    if (!form.name.trim()) { toast.error('Campaign name is required'); return }
    setSaving(true)
    const payload = {
      ...form,
      scheduled_at: form.scheduled_at || null,
      segment_id: form.segment_id || null,
      segment_name: form.segment_name || null,
      status: sendNow ? 'sent' : form.scheduled_at ? 'scheduled' : 'draft',
      sent_at: sendNow ? new Date().toISOString() : null,
      workspace_id: workspace!.id,
    }
    let error
    if (isEdit) {
      const res = await supabase.from('campaigns').update(payload).eq('id', campaign.id)
      error = res.error
    } else {
      const res = await supabase.from('campaigns').insert(payload)
      error = res.error
    }
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success(sendNow ? 'Campaign sent!' : isEdit ? 'Campaign updated' : 'Campaign created')
    queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    onSaved?.()
    onClose()
  }

  const TABS = [
    { id: 'content', label: '1. Content' },
    { id: 'audience', label: '2. Audience' },
    { id: 'schedule', label: '3. Schedule' },
  ]

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, width: '100%', maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '22px 26px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>
              {isEdit ? 'Edit campaign' : 'New campaign'}
            </h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>
          {/* Step tabs */}
          <div style={{ display: 'flex', gap: 4 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id as any)}
                style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer', background: tab === t.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'transparent', color: tab === t.id ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: tab === t.id ? '1px solid color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : '1px solid transparent' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 26px' }}>
          {tab === 'content' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Campaign name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Spring promotion 2026" style={inp}
                  onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Channel</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['email', 'sms'] as const).map(ch => {
                    const cfg = CHANNEL_CONFIG[ch]
                    return (
                      <button key={ch} onClick={() => set('channel', ch)}
                        style={{ flex: 1, padding: '10px', borderRadius: 10, cursor: 'pointer', background: form.channel === ch ? cfg.bg : 'var(--hover-bg)', border: form.channel === ch ? `1px solid color-mix(in srgb, ${cfg.color} var(--status-tint-edge), transparent)` : '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, color: form.channel === ch ? cfg.color : 'var(--ink-tertiary)', fontWeight: 600, fontSize: 13 }}>
                        <cfg.icon style={{ width: 15, height: 15 }} />
                        {cfg.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              {form.channel === 'email' && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject line</label>
                  <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Your subject line here..." style={inp}
                    onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {form.channel === 'email' ? 'Email body' : 'SMS message'}
                </label>
                <textarea value={form.body} onChange={e => set('body', e.target.value)}
                  placeholder={form.channel === 'email' ? 'Write your email here...' : 'Write your SMS message here (160 chars max)...'}
                  rows={form.channel === 'email' ? 8 : 4}
                  style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
                  onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                {form.channel === 'sms' && (
                  <p style={{ fontSize: 11, color: form.body.length > 160 ? 'var(--danger)' : 'var(--ink-tertiary)', margin: '4px 0 0', textAlign: 'right' }}>
                    {form.body.length}/160
                  </p>
                )}
              </div>
              <div style={{ padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.6 }}>
                  💡 Use <code style={{ background: 'var(--border-subtle)', padding: '1px 5px', borderRadius: 4, color: 'var(--accent-text)' }}>{'{{first_name}}'}</code>, <code style={{ background: 'var(--border-subtle)', padding: '1px 5px', borderRadius: 4, color: 'var(--accent-text)' }}>{'{{company_name}}'}</code> for personalization
                </p>
              </div>
            </div>
          )}

          {tab === 'audience' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Send to segment</label>
                {segments.length === 0 ? (
                  <div style={{ padding: '20px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, textAlign: 'center' }}>
                    <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 8px' }}>No segments yet</p>
                    <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Create segments in the Segments tab to target specific contacts</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[{ id: '', name: 'All contacts', contact_count: null }, ...segments].map((seg: any) => (
                      <div key={seg.id} onClick={() => set('segment_id', seg.id === '' ? '' : seg.id)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: form.segment_id === seg.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)' : 'var(--surface)', border: `1px solid ${form.segment_id === seg.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' : 'var(--border-subtle)'}`, borderRadius: 10, cursor: 'pointer', transition: 'all 150ms ease' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: form.segment_id === seg.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' : 'var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users style={{ width: 13, height: 13, color: form.segment_id === seg.id ? 'var(--accent-text)' : 'var(--ink-tertiary)' }} />
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{seg.name}</p>
                            {seg.contact_count !== null && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{seg.contact_count} contacts</p>}
                          </div>
                        </div>
                        {form.segment_id === seg.id && <CheckCircle2 style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'schedule' && (
            // Soft-launch HIDE — Send-now and Schedule pipelines are post-launch builds.
            // Save draft remains functional. Original tab body (datetime input + scheduled-at preview)
            // restored when the real send + scheduler land.
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', padding: '40px 20px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Send style={{ width: 22, height: 22, color: 'var(--accent-text)' }} />
              </div>
              <div style={{ textAlign: 'center', maxWidth: 380 }}>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 6px' }}>Send & Schedule — coming soon</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.55 }}>
                  Campaign sending and scheduling aren't wired to a real delivery pipeline yet. Save your campaign as a draft for now — it'll be ready to send when this lands.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 26px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={() => handleSave(false)} disabled={saving} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Saving...' : 'Save draft'}
          </button>
          {/* Soft-launch HIDE — Send-campaign button. Real send pipeline is post-launch.
              Save draft remains the only write path; handleSave(true) call site restored
              when the real send pipeline lands. */}
          {false && (
            <button onClick={() => handleSave(true)} disabled={saving}
              style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px', background: 'var(--logo-bg-gradient)', border: 'none', borderRadius: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--logo-glow)' }}>
              <Send style={{ width: 14, height: 14 }} /> Send campaign
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Sequence Builder Modal ───────────────────────────────────────────────────
function SequenceModal({ onClose, onSaved, sequence }: any) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isEdit = !!sequence
  const [form, setForm] = useState({
    name: sequence?.name || '',
    description: sequence?.description || '',
    channel: sequence?.channel || 'email',
    trigger_type: sequence?.trigger_type || 'manual',
    status: sequence?.status || 'draft',
  })
  const [steps, setSteps] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  useEffect(() => {
    if (sequence?.id) {
      supabase.from('sequence_steps').select('*').eq('sequence_id', sequence.id).order('step_number').then(({ data }) => {
        setSteps(data || [])
      })
    }
  }, [sequence?.id])

  const addStep = () => {
    setSteps(prev => [...prev, {
      id: `new-${Date.now()}`,
      step_number: prev.length + 1,
      delay_days: prev.length === 0 ? 0 : 1,
      channel: form.channel,
      subject: '',
      body: '',
      isNew: true,
    }])
  }

  const updateStep = (idx: number, field: string, value: any) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s))
  }

  const removeStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })))
  }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Sequence name is required'); return }
    setSaving(true)

    try {
      let seqId = sequence?.id
      if (isEdit) {
        const { error } = await supabase.from('sequences').update({ ...form, step_count: steps.length }).eq('id', seqId)
        if (error) throw error
      } else {
        const { data, error } = await supabase.from('sequences').insert({ ...form, step_count: steps.length, workspace_id: workspace!.id }).select().single()
        if (error) throw error
        seqId = data?.id
      }

      if (!seqId) throw new Error('Failed to obtain sequence ID')

      if (isEdit) {
        const { error } = await supabase.from('sequence_steps').delete().eq('sequence_id', seqId)
        if (error) throw error
      }

      if (steps.length > 0) {
        const { error } = await supabase.from('sequence_steps').insert(steps.map((s, i) => ({
          sequence_id: seqId,
          workspace_id: workspace!.id,
          step_number: i + 1,
          delay_days: s.delay_days || 0,
          channel: s.channel || form.channel,
          subject: s.subject || '',
          body: s.body || '',
        })))
        if (error) throw error
      }

      toast.success(isEdit ? 'Sequence updated' : 'Sequence created')
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      onSaved?.()
      onClose()
    } catch (err: any) {
      toast.error(`Failed to save sequence: ${err.message || 'unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ padding: '22px 26px 16px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{isEdit ? 'Edit sequence' : 'New sequence'}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Basics */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sequence name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. New customer welcome" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Trigger</label>
              <select value={form.trigger_type} onChange={e => set('trigger_type', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Channel</label>
              <select value={form.channel} onChange={e => set('channel', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</label>
              <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="What is this sequence for?" style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          </div>

          {/* Steps */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>Steps ({steps.length})</p>
              <button onClick={addStep}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Plus style={{ width: 13, height: 13 }} /> Add step
              </button>
            </div>

            {steps.length === 0 ? (
              <div style={{ padding: '24px', background: 'var(--surface)', border: '1px dashed var(--border-default)', borderRadius: 12, textAlign: 'center' }}>
                <Zap style={{ width: 24, height: 24, color: 'var(--border-default)', margin: '0 auto 8px', display: 'block' }} />
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 4px' }}>No steps yet</p>
                <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Add steps to build your sequence</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {steps.map((step, idx) => (
                  <div key={step.id} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 8, background: 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: 'var(--accent-text)', flexShrink: 0 }}>
                        {idx + 1}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Wait</span>
                        <input type="number" value={step.delay_days} onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)} min={0}
                          style={{ width: 50, padding: '4px 8px', background: 'var(--border-subtle)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', textAlign: 'center' }} />
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>days, then send</span>
                      </div>
                      <button onClick={() => removeStep(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                    {form.channel === 'email' && (
                      <input value={step.subject} onChange={e => updateStep(idx, 'subject', e.target.value)} placeholder="Subject line..."
                        style={{ ...inp, marginBottom: 8, fontSize: 12 }}
                        onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                        onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                    )}
                    <textarea value={step.body} onChange={e => updateStep(idx, 'body', e.target.value)}
                      placeholder={form.channel === 'email' ? 'Email body...' : 'SMS message...'}
                      rows={3}
                      style={{ ...inp, resize: 'none', lineHeight: 1.5, fontSize: 12 }}
                      onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                      onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '16px 26px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
            {saving ? 'Saving...' : <><Zap style={{ width: 14, height: 14 }} /> {isEdit ? 'Update sequence' : 'Create sequence'}</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Template Modal ───────────────────────────────────────────────────────────
function TemplateModal({ onClose, onSaved, template }: any) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isEdit = !!template
  const [form, setForm] = useState({
    name: template?.name || '',
    channel: template?.channel || 'email',
    category: template?.category || '',
    subject: template?.subject || '',
    body: template?.body || '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error('Name and body are required'); return }
    setSaving(true)
    let error
    if (isEdit) {
      const res = await supabase.from('marketing_templates').update({ ...form }).eq('id', template.id)
      error = res.error
    } else {
      const res = await supabase.from('marketing_templates').insert({ ...form, workspace_id: workspace!.id })
      error = res.error
    }
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success(isEdit ? 'Template updated' : 'Template created')
    queryClient.invalidateQueries({ queryKey: ['marketing-templates'] })
    onSaved?.(); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{isEdit ? 'Edit template' : 'New template'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Template name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Welcome email" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Channel</label>
              <select value={form.channel} onChange={e => set('channel', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select...</option>
                {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {form.channel === 'email' && (
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject line</label>
              <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Subject..." style={inp}
                onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
            </div>
          )}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Body *</label>
            <textarea value={form.body} onChange={e => set('body', e.target.value)} placeholder="Template content..." rows={6}
              style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
              onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create template'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Segment Modal ────────────────────────────────────────────────────────────
function SegmentModal({ onClose, onSaved, segment }: any) {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isEdit = !!segment
  const [name, setName] = useState(segment?.name || '')
  const [description, setDescription] = useState(segment?.description || '')
  const [filters, setFilters] = useState<any[]>(segment?.filters || [])
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<number | null>(null)

  const addFilter = () => setFilters(prev => [...prev, { field: 'status', operator: 'equals', value: 'active' }])
  const updateFilter = (idx: number, key: string, val: string) => setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f))
  const removeFilter = (idx: number) => setFilters(prev => prev.filter((_, i) => i !== idx))

  const previewCount = async () => {
    if (!workspace?.id) return
    let query = supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('workspace_id', workspace.id)
    filters.forEach(f => {
      if (f.field === 'status' && f.value) query = query.eq('status', f.value)
    })
    const { count } = await query
    setPreview(count || 0)
  }

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Segment name is required'); return }
    setSaving(true)
    let error
    if (isEdit) {
      const res = await supabase.from('segments').update({ name, description, filters, contact_count: preview ?? 0 }).eq('id', segment.id)
      error = res.error
    } else {
      const res = await supabase.from('segments').insert({ name, description, filters, contact_count: preview ?? 0, workspace_id: workspace!.id })
      error = res.error
    }
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success(isEdit ? 'Segment updated' : 'Segment created')
    queryClient.invalidateQueries({ queryKey: ['segments'] })
    onSaved?.(); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 22, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{isEdit ? 'Edit segment' : 'New segment'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Segment name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Active customers" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description..." style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>

          {/* Filters */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filters</label>
              <button onClick={addFilter}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <Plus style={{ width: 12, height: 12 }} /> Add filter
              </button>
            </div>
            {filters.length === 0 ? (
              <div style={{ padding: '14px', background: 'var(--surface)', border: '1px dashed var(--border-default)', borderRadius: 10, textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>No filters — this segment will include all contacts</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filters.map((filter, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select value={filter.field} onChange={e => updateFilter(idx, 'field', e.target.value)}
                      style={{ flex: 1, ...inp, padding: '7px 10px', fontSize: 12 }}>
                      {FILTER_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <select value={filter.operator} onChange={e => updateFilter(idx, 'operator', e.target.value)}
                      style={{ width: 100, ...inp, padding: '7px 10px', fontSize: 12 }}>
                      <option value="equals">is</option>
                      <option value="not_equals">is not</option>
                      <option value="contains">contains</option>
                    </select>
                    {filter.field === 'status' ? (
                      <select value={filter.value} onChange={e => updateFilter(idx, 'value', e.target.value)}
                        style={{ flex: 1, ...inp, padding: '7px 10px', fontSize: 12 }}>
                        <option value="active">Active</option>
                        <option value="lead">Lead</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    ) : (
                      <input value={filter.value || ''} onChange={e => updateFilter(idx, 'value', e.target.value)}
                        placeholder="Value..."
                        style={{ flex: 1, ...inp, padding: '7px 10px', fontSize: 12 }}
                        onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)'}
                        onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
                    )}
                    <button onClick={() => removeFilter(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                      <X style={{ width: 13, height: 13 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={previewCount}
              style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
              <Users style={{ width: 12, height: 12 }} /> Preview count
            </button>
            {preview !== null && (
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-text)', margin: '8px 0 0' }}>
                ✓ {preview} contacts match this segment
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create segment'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MarketingHub() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<Tab>((searchParams.get('tab') as Tab) || 'campaigns')
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [showSequenceModal, setShowSequenceModal] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [showSegmentModal, setShowSegmentModal] = useState(false)
  const [editItem, setEditItem] = useState<any>(null)
  const [channelFilter, setChannelFilter] = useState('all')

  useEffect(() => {
    const tab = searchParams.get('tab') as Tab | null
    const valid: Tab[] = ['campaigns', 'sequences', 'templates', 'segments']
    if (tab && valid.includes(tab)) setActiveTab(tab)
  }, [searchParams])

  const switchTab = (tab: Tab) => {
    setActiveTab(tab)
    setSearchParams(tab !== 'campaigns' ? { tab } : {})
  }

  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('campaigns').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: sequences = [] } = useQuery({
    queryKey: ['sequences', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('sequences').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['marketing-templates', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('marketing_templates').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: segments = [] } = useQuery({
    queryKey: ['segments', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('segments').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const deleteMutation = useMutation({
    mutationFn: async ({ table, id }: { table: string; id: string }) => {
      const { error } = await supabase.from(table).delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: (_, { table }) => {
      const keyMap: Record<string, string> = {
        campaigns: 'campaigns', sequences: 'sequences',
        marketing_templates: 'marketing-templates', segments: 'segments',
      }
      queryClient.invalidateQueries({ queryKey: [keyMap[table]] })
      toast.success('Deleted')
    },
    onError: () => toast.error('Failed to delete'),
  })

  const duplicateCampaign = async (c: any) => {
    const { error } = await supabase.from('campaigns').insert({
      ...c, id: undefined, name: `${c.name} (copy)`, status: 'draft',
      sent_at: null, created_at: undefined, updated_at: undefined,
      workspace_id: workspace!.id,
    })
    if (error) { toast.error(`Failed to duplicate: ${error.message}`); return }
    queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    toast.success('Campaign duplicated')
  }

  const filteredCampaigns = useMemo(() =>
    channelFilter === 'all' ? campaigns : campaigns.filter((c: any) => c.channel === channelFilter)
  , [campaigns, channelFilter])

  const filteredTemplates = useMemo(() =>
    channelFilter === 'all' ? templates : templates.filter((t: any) => t.channel === channelFilter)
  , [templates, channelFilter])

  const TABS = [
    { id: 'campaigns', label: 'Campaigns', count: campaigns.length },
    { id: 'sequences', label: 'Sequences', count: sequences.length },
    { id: 'templates', label: 'Templates', count: templates.length },
    { id: 'segments', label: 'Segments', count: segments.length },
  ]

  // ── Metrics ────────────────────────────────────────────────────────────────
  const metrics = useMemo(() => ({
    totalSent: campaigns.filter((c: any) => c.status === 'sent').length,
    activeSequences: sequences.filter((s: any) => s.status === 'active').length,
    totalTemplates: templates.length,
    totalSegments: segments.length,
  }), [campaigns, sequences, templates, segments])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Header */}
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Marketing Hub</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {metrics.totalSent} campaigns sent · {metrics.activeSequences} active sequences
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {activeTab === 'campaigns' && (
              <button onClick={() => { setEditItem(null); setShowCampaignModal(true) }} className="btn-primary">
                <Plus style={{ width: 15, height: 15 }} /> New campaign
              </button>
            )}
            {/* Soft-launch HIDE — Sequences require an automation engine that doesn't exist yet.
                Original "New sequence" header button restored when the automation engine ships.
                See Session 30 audit H2. */}
            {false && activeTab === 'sequences' && (
              <button onClick={() => { setEditItem(null); setShowSequenceModal(true) }} className="btn-primary">
                <Plus style={{ width: 15, height: 15 }} /> New sequence
              </button>
            )}
            {activeTab === 'templates' && (
              <button onClick={() => { setEditItem(null); setShowTemplateModal(true) }} className="btn-primary">
                <Plus style={{ width: 15, height: 15 }} /> New template
              </button>
            )}
            {activeTab === 'segments' && (
              <button onClick={() => { setEditItem(null); setShowSegmentModal(true) }} className="btn-primary">
                <Plus style={{ width: 15, height: 15 }} /> New segment
              </button>
            )}
          </div>
        </div>

        {/* Overview metrics — auto-fit so cards reflow on narrow viewports
            instead of compressing 4-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Campaigns sent',   value: metrics.totalSent,        color: 'var(--accent)',   icon: Send },
            { label: 'Active sequences', value: metrics.activeSequences,  color: 'var(--positive)', icon: Zap },
            { label: 'Templates',        value: metrics.totalTemplates,   color: 'var(--info)',     icon: FileText },
            { label: 'Segments',         value: metrics.totalSegments,    color: 'var(--warning)',  icon: Users },
          ].map(m => (
            <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, color-mix(in srgb, ${m.color} var(--status-tint-strong), transparent) 0%, transparent 70%)` }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.label}</span>
                <m.icon style={{ width: 14, height: 14, color: m.color }} />
              </div>
              <p style={{ fontSize: 24, fontWeight: 900, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => switchTab(tab.id as Tab)}
              style={{ padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease', display: 'flex', alignItems: 'center', gap: 6 }}>
              {tab.label}
              {tab.count > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, background: activeTab === tab.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' : 'var(--border-subtle)', color: activeTab === tab.id ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 28px' }}>

        {/* ── Campaigns ── */}
        {activeTab === 'campaigns' && (
          <div>
            {/* Channel filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {['all', 'email', 'sms'].map(ch => (
                <button key={ch} onClick={() => setChannelFilter(ch)}
                  style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: channelFilter === ch ? 'var(--accent)' : 'var(--hover-bg)', color: channelFilter === ch ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: channelFilter === ch ? 'none' : '1px solid var(--border-subtle)' }}>
                  {ch === 'all' ? 'All channels' : ch}
                </button>
              ))}
            </div>

            {filteredCampaigns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <Send style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No campaigns yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>Create your first campaign to start reaching your customers</p>
                <button onClick={() => { setEditItem(null); setShowCampaignModal(true) }} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New campaign
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredCampaigns.map((c: any) => {
                  const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft
                  const channelCfg = CHANNEL_CONFIG[c.channel] || CHANNEL_CONFIG.email
                  const ChannelIcon = channelCfg.icon
                  return (
                    <motion.div key={c.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, transition: 'background 150ms ease' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: channelCfg.bg, border: `1px solid color-mix(in srgb, ${channelCfg.color} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <ChannelIcon style={{ width: 18, height: 18, color: channelCfg.color }} />
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</p>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color, flexShrink: 0 }}>{statusCfg.label}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {c.subject && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject}</p>}
                          {c.segment_name && (
                            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                              <Users style={{ width: 10, height: 10 }} /> {c.segment_name || 'All contacts'}
                            </span>
                          )}
                        </div>
                      </div>
                      {c.status === 'sent' && (
                        <div style={{ display: 'flex', gap: 16, flexShrink: 0 }}>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{c.recipient_count || 0}</p>
                            <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>Sent</p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent-text)', margin: 0 }}>{c.open_count || 0}</p>
                            <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>Opens</p>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--positive)', margin: 0 }}>{c.click_count || 0}</p>
                            <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>Clicks</p>
                          </div>
                        </div>
                      )}
                      {c.scheduled_at && c.status === 'scheduled' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'color-mix(in srgb, var(--warning) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', borderRadius: 8, flexShrink: 0 }}>
                          <Clock style={{ width: 12, height: 12, color: 'var(--warning)' }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--warning)' }}>{formatDate(c.scheduled_at, 'MMM d, h:mm aa')}</span>
                        </div>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }}>{formatRelativeDate(c.created_at)}</span>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        {c.status === 'draft' && (
                          <button onClick={() => { setEditItem(c); setShowCampaignModal(true) }}
                            style={{ padding: '6px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Edit
                          </button>
                        )}
                        <button onClick={() => duplicateCampaign(c)}
                          title="Duplicate"
                          style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Copy style={{ width: 13, height: 13 }} />
                        </button>
                        <button onClick={() => { if (window.confirm('Delete this campaign?')) deleteMutation.mutate({ table: 'campaigns', id: c.id }) }}
                          title="Delete"
                          style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                          <Trash2 style={{ width: 13, height: 13 }} />
                        </button>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Sequences ── */}
        {/* Soft-launch HIDE — Sequence automation engine is post-launch. Same pattern as the
            Marketing "Send & Schedule" hide. The full original tab body (empty-state CTA + sequence
            cards with Activate/Pause/Resume controls) is preserved below behind a `{false &&}`
            guard so it restores cleanly when the engine ships. Operators see only the coming-soon
            panel today. See Session 30 audit H2. */}
        {activeTab === 'sequences' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', padding: '60px 20px' }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap style={{ width: 22, height: 22, color: 'var(--accent-text)' }} />
            </div>
            <div style={{ textAlign: 'center', maxWidth: 420 }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 6px' }}>Sequences — coming soon</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.55 }}>
                Multi-step drip sequences need an automation engine that's still in active build. We'll bring this tab back when sequences can actually run.
              </p>
            </div>
          </div>
        )}
        {false && activeTab === 'sequences' && (
          <div>
            {sequences.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <Zap style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No sequences yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>Automate follow-ups with drip sequences</p>
                <button onClick={() => { setEditItem(null); setShowSequenceModal(true) }} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New sequence
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {sequences.map((seq: any) => {
                  const statusCfg = STATUS_CONFIG[seq.status] || STATUS_CONFIG.draft
                  const channelCfg = CHANNEL_CONFIG[seq.channel] || CHANNEL_CONFIG.email
                  const ChannelIcon = channelCfg.icon
                  const trigger = TRIGGER_TYPES.find(t => t.value === seq.trigger_type)
                  return (
                    <motion.div key={seq.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: 10, background: channelCfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <ChannelIcon style={{ width: 16, height: 16, color: channelCfg.color }} />
                          </div>
                          <div>
                            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 3px' }}>{seq.name}</p>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color }}>{statusCfg.label}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => { setEditItem(seq); setShowSequenceModal(true) }}
                            style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Edit3 style={{ width: 12, height: 12 }} />
                          </button>
                          <button onClick={() => { if (window.confirm('Delete this sequence?')) deleteMutation.mutate({ table: 'sequences', id: seq.id }) }}
                            style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                            <Trash2 style={{ width: 12, height: 12 }} />
                          </button>
                        </div>
                      </div>
                      {seq.description && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 12px', lineHeight: 1.5 }}>{seq.description}</p>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Zap style={{ width: 12, height: 12, color: 'var(--warning)' }} />
                          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Trigger: {trigger?.label || seq.trigger_type}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FileText style={{ width: 12, height: 12, color: 'var(--accent)' }} />
                          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{seq.step_count || 0} steps</span>
                        </div>
                        {seq.enrolled_count > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Users style={{ width: 12, height: 12, color: 'var(--positive)' }} />
                            <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{seq.enrolled_count} enrolled</span>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {seq.status === 'draft' && (
                          <button onClick={async () => {
                            const { error } = await supabase.from('sequences').update({ status: 'active' }).eq('id', seq.id)
                            if (error) { toast.error('Failed to activate'); return }
                            queryClient.invalidateQueries({ queryKey: ['sequences'] })
                            toast.success('Sequence activated')
                          }}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--positive) var(--status-tint-strong), transparent)', borderRadius: 9, color: 'var(--positive)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            <Play style={{ width: 12, height: 12 }} /> Activate
                          </button>
                        )}
                        {seq.status === 'active' && (
                          <button onClick={async () => {
                            const { error } = await supabase.from('sequences').update({ status: 'paused' }).eq('id', seq.id)
                            if (error) { toast.error('Failed to pause'); return }
                            queryClient.invalidateQueries({ queryKey: ['sequences'] })
                            toast.success('Sequence paused')
                          }}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', background: 'color-mix(in srgb, var(--warning) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--warning) var(--status-tint-strong), transparent)', borderRadius: 9, color: 'var(--warning)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            <Pause style={{ width: 12, height: 12 }} /> Pause
                          </button>
                        )}
                        {seq.status === 'paused' && (
                          <button onClick={async () => {
                            const { error } = await supabase.from('sequences').update({ status: 'active' }).eq('id', seq.id)
                            if (error) { toast.error('Failed to resume'); return }
                            queryClient.invalidateQueries({ queryKey: ['sequences'] })
                            toast.success('Sequence resumed')
                          }}
                            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--positive) var(--status-tint-strong), transparent)', borderRadius: 9, color: 'var(--positive)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            <Play style={{ width: 12, height: 12 }} /> Resume
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Templates ── */}
        {activeTab === 'templates' && (
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {['all', 'email', 'sms'].map(ch => (
                <button key={ch} onClick={() => setChannelFilter(ch)}
                  style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: channelFilter === ch ? 'var(--accent)' : 'var(--hover-bg)', color: channelFilter === ch ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: channelFilter === ch ? 'none' : '1px solid var(--border-subtle)' }}>
                  {ch === 'all' ? 'All channels' : ch}
                </button>
              ))}
            </div>

            {filteredTemplates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <FileText style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No templates yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>Save reusable messages as templates</p>
                <button onClick={() => { setEditItem(null); setShowTemplateModal(true) }} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New template
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {filteredTemplates.map((t: any) => {
                  const channelCfg = CHANNEL_CONFIG[t.channel] || CHANNEL_CONFIG.email
                  const ChannelIcon = channelCfg.icon
                  return (
                    <motion.div key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '16px 18px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 9, background: channelCfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <ChannelIcon style={{ width: 14, height: 14, color: channelCfg.color }} />
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{t.name}</p>
                            {t.category && <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{t.category}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => { setEditItem(t); setShowTemplateModal(true) }}
                            style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Edit3 style={{ width: 11, height: 11 }} />
                          </button>
                          <button onClick={() => { if (window.confirm('Delete this template?')) deleteMutation.mutate({ table: 'marketing_templates', id: t.id }) }}
                            style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                            <Trash2 style={{ width: 11, height: 11 }} />
                          </button>
                        </div>
                      </div>
                      {t.subject && <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>{t.subject}</p>}
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {t.body}
                      </p>
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(t.updated_at || t.created_at)}</span>
                        <button onClick={() => {
                          setEditItem(null)
                          setShowCampaignModal(true)
                        }}
                          style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                          Use in campaign <ArrowRight style={{ width: 11, height: 11 }} />
                        </button>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Segments ── */}
        {activeTab === 'segments' && (
          <div>
            {segments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <Users style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No segments yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>Group contacts into segments to target them in campaigns</p>
                <button onClick={() => { setEditItem(null); setShowSegmentModal(true) }} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New segment
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {segments.map((seg: any) => (
                  <motion.div key={seg.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Filter style={{ width: 15, height: 15, color: 'var(--accent-text)' }} />
                        </div>
                        <div>
                          <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{seg.name}</p>
                          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)', margin: 0 }}>{seg.contact_count || 0} contacts</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button onClick={() => { setEditItem(seg); setShowSegmentModal(true) }}
                          style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Edit3 style={{ width: 12, height: 12 }} />
                        </button>
                        <button onClick={() => { if (window.confirm('Delete this segment?')) deleteMutation.mutate({ table: 'segments', id: seg.id }) }}
                          style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                          <Trash2 style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    </div>
                    {seg.description && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 12px', lineHeight: 1.5 }}>{seg.description}</p>}
                    {seg.filters?.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
                        {seg.filters.map((f: any, i: number) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-tertiary)', background: 'var(--surface)', padding: '4px 8px', borderRadius: 6 }}>
                            <Tag style={{ width: 10, height: 10, color: 'var(--accent)' }} />
                            <span style={{ color: 'var(--accent-text)' }}>{f.field}</span>
                            <span>{f.operator}</span>
                            <span style={{ color: 'var(--ink-primary)', fontWeight: 600 }}>{f.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {seg.filters?.length === 0 && (
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 12px', fontStyle: 'italic' }}>No filters — includes all contacts</p>
                    )}
                    <button onClick={() => { setEditItem(null); setShowCampaignModal(true) }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px', background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 9, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <Send style={{ width: 12, height: 12 }} /> Send campaign to segment
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCampaignModal && (
          <CampaignModal
            onClose={() => { setShowCampaignModal(false); setEditItem(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['campaigns'] })}
            campaign={editItem}
            segments={segments}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSequenceModal && (
          <SequenceModal
            onClose={() => { setShowSequenceModal(false); setEditItem(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['sequences'] })}
            sequence={editItem}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTemplateModal && (
          <TemplateModal
            onClose={() => { setShowTemplateModal(false); setEditItem(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['marketing-templates'] })}
            template={editItem}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSegmentModal && (
          <SegmentModal
            onClose={() => { setShowSegmentModal(false); setEditItem(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['segments'] })}
            segment={editItem}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
